import { useEffect } from 'react';

/**
 * Smooth "logo rides the rail" scroll marker, shared by the homepage Journey
 * rail and the feature-landing StepsTimeline.
 *
 * Why not state + CSS transition (the old approach): a React state update per
 * scroll step re-renders the whole section, and a short `top` transition
 * animates large pixel deltas late — together they read as jumpy, and on tall
 * mobile layouts the marker visibly "flies". Instead:
 *
 *  - The TARGET is viewport-anchored: the marker wants to sit at a fixed
 *    fraction of the viewport height (`anchor`), clamped to the rail's bounds.
 *    While the section is on screen the marker appears to calmly hold its
 *    lane as the rail scrolls past — it can never outrun the section.
 *  - The RENDERED position eases toward the target with a rAF lerp written
 *    straight to `style.transform`. Zero React re-renders per frame.
 *
 * The marker element must be absolutely positioned with `top: 0` and have its
 * horizontal centering included here (translateX(-50%)) — don't add competing
 * transform classes to it.
 *
 * @param railRef   ref to the rail wrapper (position: relative)
 * @param markerRef ref to the marker element
 * @param opts.anchor   viewport fraction the marker rides at (default 0.42)
 * @param opts.inset    px the marker stays inside the rail's ends (default 8)
 * @param opts.offset   px from marker top to its visual center (default 18 = half of 36px logo)
 * @param opts.onFrame  optional (yCenterPx, railRect) called on applied frames —
 *                      use for cheap derived UI (e.g. chapter dot activation).
 */
export default function useRailMarker(railRef, markerRef, opts = {}) {
  const { anchor = 0.42, inset = 8, offset = 18, onFrame } = opts;

  useEffect(() => {
    const rail = railRef.current;
    const marker = markerRef.current;
    if (!rail || !marker) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let current = null; // marker center, px from rail top
    let target = 0;
    let raf = 0;
    let animating = false;

    const computeTarget = () => {
      const rect = rail.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      target = Math.max(inset, Math.min(rect.height - inset, vh * anchor - rect.top));
      return rect;
    };

    const apply = (rect) => {
      marker.style.transform = `translate(-50%, ${current - offset}px)`;
      if (onFrame) onFrame(current, rect || rail.getBoundingClientRect());
    };

    const tick = () => {
      raf = 0;
      const rect = computeTarget();
      if (current === null || reduced) {
        current = target;
      } else {
        current += (target - current) * 0.16;
        if (Math.abs(target - current) < 0.5) current = target;
      }
      apply(rect);
      if (current !== target) {
        animating = true;
        raf = window.requestAnimationFrame(tick);
      } else {
        animating = false;
      }
    };

    const kick = () => {
      if (!animating && !raf) raf = window.requestAnimationFrame(tick);
    };

    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick);
    kick();

    return () => {
      window.removeEventListener('scroll', kick);
      window.removeEventListener('resize', kick);
      if (raf) window.cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
